/**
 * Pure predicate + URL (de)serialization logic for the list view's filter
 * bar (status / area / live search). Kept dependency-free so it's easy to
 * unit test without touching React or the DOM.
 */

import { STATUSES, type Project, type Status } from "../shared/types.ts";

/**
 * Statuses the main list ever shows. "done" projects live on the Archive
 * page — the list filter doesn't duplicate that view, so it never offers
 * "done" as an option.
 */
export const LIST_STATUSES = STATUSES.filter((status) => status !== "done") as Exclude<Status, "done">[];

export type StatusFilter = "all" | Exclude<Status, "done">;

export const ALL_AREAS = "all";

export interface ListFilters {
  status: StatusFilter;
  area: string;
  q: string;
}

export const DEFAULT_FILTERS: ListFilters = { status: "all", area: ALL_AREAS, q: "" };

/** Whether any filter is narrowing the list below "everything active". */
export function isFiltering(filters: ListFilters): boolean {
  return filters.status !== "all" || filters.area !== ALL_AREAS || filters.q.trim() !== "";
}

/** How many of the three filters are set — powers the toggle button's count badge. */
export function activeFilterCount(filters: ListFilters): number {
  return (
    (filters.status !== "all" ? 1 : 0) +
    (filters.area !== ALL_AREAS ? 1 : 0) +
    (filters.q.trim() !== "" ? 1 : 0)
  );
}

/** Status + area are exact matches; search is a case-insensitive substring match, ANDed together. */
export function matchesFilters(project: Project, filters: ListFilters): boolean {
  if (filters.status !== "all" && project.status !== filters.status) return false;
  if (filters.area !== ALL_AREAS && project.area !== filters.area) return false;

  const q = filters.q.trim().toLowerCase();
  if (q) {
    const haystack = `${project.title} ${project.area} ${project.notes_md}`.toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  return true;
}

export function filterProjects(projects: Project[], filters: ListFilters): Project[] {
  return projects.filter((project) => matchesFilters(project, filters));
}

const isListStatus = (value: string): value is Exclude<Status, "done"> =>
  (LIST_STATUSES as readonly string[]).includes(value);

/**
 * Reads filter state back out of a `status=...&area=...&q=...` query
 * string. Tolerant of a leading `?` (or none) and of a stale/tampered
 * value — anything unrecognized just falls back to "no filter".
 */
export function filtersFromSearch(search: string): ListFilters {
  const params = new URLSearchParams(search.replace(/^\?/, ""));
  const status = params.get("status");
  const area = params.get("area");
  return {
    status: status && isListStatus(status) ? status : "all",
    area: area && area.trim() ? area : ALL_AREAS,
    q: params.get("q") ?? "",
  };
}

/** Renders filter state to a query string (including the leading `?`), or "" when nothing is set. */
export function filtersToSearch(filters: ListFilters): string {
  const params = new URLSearchParams();
  if (filters.status !== "all") params.set("status", filters.status);
  if (filters.area !== ALL_AREAS) params.set("area", filters.area);
  if (filters.q.trim()) params.set("q", filters.q);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}
